import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faTrashAlt,
  faEdit,
  faEnvelope,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Addstudent from "../Addstudent/Addstudent";
import ViewStudent from "../ViewStudent/ViewStudent";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "../Student/Student.css";
import "bootstrap/dist/css/bootstrap.min.css";
import * as XLSX from "xlsx";

const API_URL = "https://aadyaacademy.org/api";

function Student() {
  const [studentList, setStudentList] = useState([]);
  const [filteredStudent, setFilteredStudent] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [addingStudent, setAddingStudent] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [sendingMessageToId, setSendingMessageToId] = useState(null);
  const navigate = useNavigate();

  // Fetch students, sections, and classes from the API
  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsResponse, sectionsResponse, classesResponse] = await Promise.all([
        axios.get(`${API_URL}/students`),
        axios.get(`${API_URL}/sections`),
        axios.get(`${API_URL}/classes`),
      ]);

      setStudentList(studentsResponse.data || []);
      setSections(sectionsResponse.data.sections.map((section) => section.name));
      setClasses(classesResponse.data.classes.map((cls) => cls.name));
    } catch (error) {
      toast.error("Error fetching data. Please try again.");
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter and sort students based on search term, class, and section
  useEffect(() => {
    let filtered = [...studentList];
    if (searchTerm) {
      filtered = filtered.filter((student) => {
        const searchValue = searchTerm.toLowerCase();
        return (
          student.student_id.toString().includes(searchValue) ||
          student.student_name?.toLowerCase().includes(searchValue) ||
          student.class?.toLowerCase().includes(searchValue) ||
          student.section?.toLowerCase().includes(searchValue) ||
          student.gender?.toLowerCase().includes(searchValue) ||
          (student.dob && new Date(student.dob).toISOString().split("T")[0].includes(searchValue))
        );
      });
    }
    if (classFilter) {
      filtered = filtered.filter((student) => student.class === classFilter);
    }
    if (sectionFilter) {
      filtered = filtered.filter((student) => student.section === sectionFilter);
    }

    filtered.sort((a, b) => {
      const nameA = (a.student_name || "").toLowerCase();
      const nameB = (b.student_name || "").toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    setFilteredStudent(filtered);
  }, [searchTerm, classFilter, sectionFilter, studentList]);

  const handleCancel = () => {
    setAddingStudent(false);
    setEditingStudent(null);
    setViewingStudent(null);
    setDeletingStudent(null);
  };

  const handleDelete = async () => {
    try {
      console.log("Attempting to delete student:", deletingStudent.student_id);
      await axios.delete(`${API_URL}/students/${deletingStudent.student_id}`);
      console.log("Delete successful");
      fetchData();
      toast.success("Student deleted successfully!");
      setDeletingStudent(null);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting student. Please try again.");
    }
  };

  const handleConfirmDelete = (student) => {
    console.log("Confirm delete student:", student);
    setDeletingStudent(student);
  };

  const handleEdit = (student) => {
    console.log("Student data passed to edit:", student);
    setEditingStudent(student);
    setAddingStudent(true);
  };

  const handleView = (student) => {
    setViewingStudent(student);
  };

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const formatDateToDisplay = (date) => {
    if (!date) return "Invalid Date";
    try {
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    } catch {
      return "Invalid Date";
    }
  };

  const handleDateChange = (e) => {
    const rawDate = e.target.value;
    setSelectedDate(formatDateToDisplay(rawDate));
  };

  const sendMessageToStudent = (student) => {
    setSendingMessageToId(student.student_id);
    toast.info(`Sending message to ${student.student_name}...`);
    setTimeout(() => {
      toast.info("We are developing...");
      setSendingMessageToId(null);
    }, 1000);
  };

  const handleRowClick = (student, e) => {
    if (e.target.closest(".action-btn")) {
      return;
    }
    handleView(student);
  };

  const exportToExcel = () => {
  if (filteredStudent.length === 0) {
    toast.info("No students to export");
    return;
  }

  const fieldsOrder = [
    "student_id",
    "student_name",
    "student_aadhar",
    "class",
    "section",
    "gender",
    "dob",
    "student_blood_group",
    "academic_year_joined",
    "caste",
    "nationality",
    "mother_tongue",
    "address",
    "home_town",
    "father_name",
    "father_phone",
    "father_email",
    "father_occupation",
    "father_aadhar",
    "mother_name",
    "mother_phone",
    "mother_email",
    "mother_occupation",
    "mother_aadhar",
    "guardian_name",
    "guardian_phone",
    "guardian_email",
    "guardian_aadhar",
    "image",
  ];

  const exportData = filteredStudent.map((student) => {
    const cleanStudent = {};
    fieldsOrder.forEach((field) => {
      let value = student[field];
      if (value === null || value === undefined) {
        value = "";
      } else if (field === "dob" && value) {
        // Format dob as dd/mm/yyyy
        const [year, month, day] = value.split("-");
        value = `${day}/${month}/${year}`;
      }
      cleanStudent[field] = value;
    });
    return cleanStudent;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData, { header: fieldsOrder });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

  XLSX.writeFile(workbook, "students_data.xlsx");
};


  if (addingStudent) {
    return (
      <Addstudent
        handleCancel={handleCancel}
        fetchData={fetchData}
        editingStudent={editingStudent}
      />
    );
  }

  if (viewingStudent) {
    return <ViewStudent student={viewingStudent} onClose={handleCancel} />;
  }

  return (
    <div className="student">
      <div className="student-table">
        <div className="table-header">
          <div className="table-header-top">
            <div
              className="header-container"
              style={{ display: "flex", alignItems: "center" }}
            >
              <h2>Student List: {filteredStudent.length}</h2>
            </div>
          </div>
          <div
            className="table-header-btm"
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <input
              type="date"
              className="date-picker"
              value={
                selectedDate ? selectedDate.split("/").reverse().join("-") : ""
              }
              onChange={handleDateChange}
              max={getCurrentDate()}
            />
            <select
              className="class-filter"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">Class</option>
              {classes.map((cls, index) => (
                <option key={index} value={cls}>
                  {cls}
                </option>
              ))}
            </select>
            <select
              className="section-filter"
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
            >
              <option value="">Section</option>
              {sections.map((section, index) => (
                <option key={index} value={section}>
                  {section}
                </option>
              ))}
            </select>
            <button
              onClick={() => setAddingStudent(true)}
              className="add-student-btn"
              title="Add New Student"
            >
              <FontAwesomeIcon icon={faPlus} /> Add New
            </button>
            <button
              onClick={exportToExcel}
              className="download-btn"
              title="Download Students Data"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              <FontAwesomeIcon icon={faDownload} /> Download
            </button>
          </div>
        </div>
        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th className="thead-content">ID</th>
                <th className="thead-content">Name</th>
                <th className="thead-content">Class</th>
                <th className="thead-content">Section</th>
                <th className="thead-content">Gender</th>
                <th className="thead-content">DOB</th>
                <th className="thead-content">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : filteredStudent.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No students found
                  </td>
                </tr>
              ) : (
                filteredStudent.map((student) => (
                  <tr
                    key={student.student_id}
                    onClick={(e) => handleRowClick(student, e)}
                    style={{ cursor: "pointer" }}
                    className="student-row"
                  >
                    <td className="tbody-content">{student.student_id}</td>
                    <td className="tbody-content">{student.student_name}</td>
                    <td className="tbody-content">{student.class}</td>
                    <td className="tbody-content">{student.section}</td>
                    <td className="tbody-content">{student.gender}</td>
                    <td className="tbody-content">
                      {student.dob ? formatDateToDisplay(student.dob) : "N/A"}
                    </td>
                    <td className="tbody-content actions">
                      <button
                        onClick={() => handleView(student)}
                        className="action-btn view-btn"
                        title="View"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => handleEdit(student)}
                        className="action-btn edit-btn"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleConfirmDelete(student)}
                        className="action-btn delete-btn"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                      <button
                        onClick={() => sendMessageToStudent(student)}
                        disabled={sendingMessageToId === student.student_id}
                        className="action-btn message-btn"
                        title="Send Message"
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete confirmation modal */}
        {deletingStudent && (
          <div className="modal delete-confirmation">
            <div className="modal-content">
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete student "
                {deletingStudent.student_name}"?
              </p>
              <div className="modal-actions">
                <button onClick={handleDelete} className="btn btn-danger">
                  Delete
                </button>
                <button
                  onClick={() => setDeletingStudent(null)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
}

export default Student;
